import { useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useOutletContext, useParams } from "react-router-dom";
import AdSenseUnit from "../components/AdSenseUnit.jsx";
import QuizCard from "../components/QuizCard.jsx";
import { fetchAllQuizzes } from "../api.js";

const formatCategoryLabel = (category) => {
  if (!category) return "All";
  return category
    .split("-")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
};

const CategoryPage = () => {
  const { category = "" } = useParams();
  const [quizzes, setQuizzes] = useState([]);
  const [status, setStatus] = useState("loading");
  const outletContext = useOutletContext();
  const setAvailableCategories = outletContext?.setAvailableCategories;

  useEffect(() => {
    const load = async () => {
      try {
        setStatus("loading");
        const data = await fetchAllQuizzes();
        setQuizzes(data);
        const derivedCategories = Array.from(
          new Set(data.map((quiz) => quiz.category).filter(Boolean))
        ).sort();
        setAvailableCategories?.(derivedCategories);
        setStatus("success");
      } catch (error) {
        console.error(error);
        setStatus("error");
      }
    };

    load();
  }, [setAvailableCategories]);

  const filteredQuizzes = useMemo(() => {
    return quizzes.filter((quiz) => quiz.category === category);
  }, [quizzes, category]);

  const pageTitle = `${formatCategoryLabel(category)} Quizzes`;

  let content;
  if (status === "loading") {
    content = (
      <div className="grid gap-6 md:grid-cols-2">
        {Array.from({ length: 4 }).map((_, index) => (
          <div key={index} className="h-48 animate-pulse rounded-2xl bg-white/5" />
        ))}
      </div>
    );
  } else if (status === "error") {
    content = (
      <div className="rounded-2xl border border-red-500/40 bg-red-500/10 p-6 text-center text-red-200">
        Something went wrong while loading quizzes. Please try again.
      </div>
    );
  } else if (!filteredQuizzes.length) {
    content = (
      <div className="rounded-2xl border border-white/10 bg-white/5 p-8 text-center text-white/70">
        No quizzes yet in this category.
      </div>
    );
  } else {
    content = (
      <div className="grid gap-6 md:grid-cols-2">
        {filteredQuizzes.map((quiz) => (
          <QuizCard key={quiz.id} quiz={quiz} />
        ))}
      </div>
    );
  }

  const categoryLabel = formatCategoryLabel(category);

  return (
    <div className="w-full space-y-10">
      <Helmet>
        <title>{categoryLabel} Quizzes — Test Your {categoryLabel} Knowledge | QuizFreak</title>
        <meta name="description" content={`Take fun ${categoryLabel.toLowerCase()} quizzes and see how you stack up. Free online quizzes updated regularly.`} />
        <link rel="canonical" href={`https://quizfreak.com/v2/category/${category}/`} />
      </Helmet>
      <div className="rounded-3xl border border-white/10 bg-white/5 p-8 text-white">
        <p className="text-sm uppercase tracking-[0.3em] text-white/60">Category</p>
        <h1 className="mt-3 text-4xl font-semibold leading-tight">{pageTitle}</h1>
        <p className="mt-3 max-w-2xl text-lg text-white/80">
          Fresh quizzes curated for the {formatCategoryLabel(category)} category. Pick one and
          prove you can crush it.
        </p>
      </div>
      <AdSenseUnit />
      {content}
    </div>
  );
};

export default CategoryPage;
